// Fuente única de verdad de variantes/condiciones/idiomas POR JUEGO.
// Agregar un TCG nuevo mañana = un objeto más acá; los formularios de crear
// y editar (vendedor + admin) lo consumen vía los helpers de abajo y se
// adaptan solos. Evita el patrón "hardcodeo por formulario" que causó el
// bug de "editar carta Riftbound muestra variantes Pokémon" (feb 2026).
//
// Debe mantenerse en espejo con `/app/backend/game_config.py` para que
// validación cliente y server usen el mismo whitelist.

import { conditionOptions } from "@/lib/conditions";

const STANDARD_CONDITIONS = conditionOptions();

const STANDARD_LANGUAGES = [
  { value: "es", label: "Español" },
  { value: "en", label: "Inglés" },
  { value: "ja", label: "Japonés" },
  { value: "otro", label: "Otro" },
];

export const GAME_CONFIG = {
  pokemon: {
    variants: [
      { value: "normal", label: "Normal (holo o non-holo estándar)" },
      // Reverse Holo se ofrece SOLO si el API TCGdex declara que la carta
      // trae esa versión (variants_available.reverse === true). Es el patrón
      // que PublishCard.jsx usa desde hace tiempo.
      { value: "reverse_holo", label: "Reverse Holo",
        conditional: (card) => card?.variants_available?.reverse === true },
      { value: "price_pack", label: "Price Pack" },
      // "Patrón" — solo Pokémon. Se usa en cartas custom que traen un patrón
      // impreso (Pokébola, Masterbola, patrón lucha, etc.). La descripción
      // del patrón se guarda en el campo `pattern_label` de la carta.
      { value: "patron", label: "Patrón" },
    ],
    conditions: STANDARD_CONDITIONS,
    languages: STANDARD_LANGUAGES,
  },
  riftbound: {
    variants: [
      { value: "normal", label: "Normal" },
      { value: "promo", label: "Promo" },
      { value: "overnumbered", label: "Overnumbered (\u2605)" },
      { value: "alt_art", label: "Alterna (Alt-Art)" },
    ],
    // Riftbound comparte el estándar M/NM/EX/GD/LP/PL — es la escala industrial
    // (Beckett/PSA), no específica del juego.
    conditions: STANDARD_CONDITIONS,
    languages: STANDARD_LANGUAGES,
  },
  yugioh: {
    // Variants: mapean a backend `/app/backend/game_config.py` → whitelist
    // `{"normal","1st_edition","unlimited","limited","alt_art","promo"}`.
    variants: [
      { value: "normal", label: "Normal" },
      { value: "1st_edition", label: "1st Edition" },
      { value: "unlimited", label: "Unlimited" },
      { value: "limited", label: "Limited Edition" },
      { value: "alt_art", label: "Alt-Art" },
      { value: "promo", label: "Promo" },
    ],
    conditions: STANDARD_CONDITIONS,
    languages: STANDARD_LANGUAGES,
  },
};

/** Resuelve el juego de una carta con fallback en orden explícito → slug → pokemon. */
export function gameOfCard(card) {
  return card?.game || card?.game_slug || "pokemon";
}

/** Variantes válidas para el juego de esta carta, evaluando conditionals con el
 *  doc real. `currentVariant` (opcional) se preserva como "grandfather" si no
 *  está en la lista canónica — así editar una publicación legacy nunca falla. */
export function variantsForCard(card, currentVariant) {
  const game = gameOfCard(card);
  const cfg = GAME_CONFIG[game] || GAME_CONFIG.pokemon;
  const items = cfg.variants
    .filter(v => !v.conditional || v.conditional(card))
    .map(({ value, label }) => ({ value, label }));
  if (currentVariant && !items.find(i => i.value === currentVariant)) {
    items.push({ value: currentVariant, label: currentVariant });
  }
  return items;
}

/** Condiciones válidas para el juego de esta carta. Hoy son las mismas 6
 *  estándar para pokemon/riftbound; si un juego futuro cambia la escala,
 *  se define en su bloque de GAME_CONFIG. */
export function conditionsForCard(card) {
  const game = gameOfCard(card);
  const cfg = GAME_CONFIG[game] || GAME_CONFIG.pokemon;
  return cfg.conditions;
}

/** Idiomas válidos para el juego de esta carta. */
export function languagesForCard(card) {
  const game = gameOfCard(card);
  const cfg = GAME_CONFIG[game] || GAME_CONFIG.pokemon;
  return cfg.languages;
}
